import { Link } from 'react-router-dom';
import {
  BookOpen,
  Key,
  Package,
  Terminal,
  Code,
  Layers,
  FileCode,
  Archive,
  ArrowRight,
} from 'lucide-react';

const guides = [
  {
    title: 'Getting Started',
    description: 'Learn the basics of Opair platform and set up your first project.',
    href: '/developers/getting-started',
    icon: BookOpen,
    color: 'bg-blue-500',
  },
  {
    title: 'Authentication',
    description: 'Understand API keys, JWT tokens, and secure authentication flows.',
    href: '/developers/authentication',
    icon: Key,
    color: 'bg-green-500',
  },
  {
    title: 'SDK Quickstart',
    description: 'Integrate Opair into your application using our TypeScript SDK.',
    href: '/developers/sdk-quickstart',
    icon: Package,
    color: 'bg-purple-500',
  },
  {
    title: 'CLI Reference',
    description: 'Master the opair CLI for policy management and deployments.',
    href: '/developers/cli-reference',
    icon: Terminal,
    color: 'bg-orange-500',
  },
  {
    title: 'API Reference',
    description: 'Complete REST API documentation with examples and schemas.',
    href: '/developers/api-reference',
    icon: Code,
    color: 'bg-red-500',
  },
  {
    title: 'Architecture',
    description: 'Deep dive into Opair architecture, data flow, and components.',
    href: '/developers/architecture',
    icon: Layers,
    color: 'bg-indigo-500',
  },
  {
    title: 'Policy Authoring',
    description: 'Write powerful governance policies using our policy DSL.',
    href: '/developers/policy-authoring',
    icon: FileCode,
    color: 'bg-teal-500',
  },
  {
    title: 'Bundle Compilation',
    description: 'Compile and deploy policy bundles for production use.',
    href: '/developers/bundle-compilation',
    icon: Archive,
    color: 'bg-pink-500',
  },
];

export default function DevelopersIndex() {
  return (
    <div>
      <div className="mb-12">
        <h1 className="text-3xl font-bold text-slate-900">Developer Documentation</h1>
        <p className="mt-4 text-lg text-slate-600">
          Everything you need to integrate and extend the Opair AI Governance Platform.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {guides.map((guide) => {
          const Icon = guide.icon;
          return (
            <Link
              key={guide.title}
              to={guide.href}
              className="group relative p-6 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all"
            >
              <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-lg ${guide.color}`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                    {guide.title}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">{guide.description}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-12 p-6 bg-indigo-50 rounded-xl border border-indigo-100">
        <h2 className="text-lg font-semibold text-indigo-900">Interactive API Explorer</h2>
        <p className="mt-2 text-indigo-700">
          Try our APIs directly in your browser with the interactive Swagger UI documentation.
        </p>
        <a
          href="/api/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Open Swagger UI
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
